import { ConfigProvider, theme as antTheme, type ThemeConfig } from "antd";
import { createContext, useContext, useEffect, useState, type PropsWithChildren } from "react";

import { auth } from "./api";

export type Design = "lightence" | "executive" | "slate";
export type ColorMode = "light" | "dark";

interface Appearance {
  design: Design;
  mode: ColorMode;
}

interface ThemeContextValue extends Appearance {
  setDesign: (design: Design) => void;
  setMode: (mode: ColorMode) => void;
  refreshProfile: () => void;
}

const DEFAULT_APPEARANCE: Appearance = { design: "lightence", mode: "light" };
const ThemeContext = createContext<ThemeContextValue | null>(null);

function storageKey() {
  return `waviot_plan_appearance:${auth.login() || "guest"}`;
}

function readAppearance(): Appearance {
  try {
    const saved = JSON.parse(localStorage.getItem(storageKey()) ?? "{}") as Partial<Appearance>;
    return {
      design: saved.design === "executive" || saved.design === "slate" ? saved.design : "lightence",
      mode: saved.mode === "dark" ? "dark" : "light",
    };
  } catch {
    return DEFAULT_APPEARANCE;
  }
}

function configFor({ design, mode }: Appearance): ThemeConfig {
  const dark = mode === "dark";
  const primary = design === "executive" ? "#1f4e78" : design === "slate" ? "#38bdf8" : "#4f46e5";
  return {
    algorithm: dark ? antTheme.darkAlgorithm : antTheme.defaultAlgorithm,
    token: {
      colorPrimary: primary,
      colorInfo: primary,
      colorBgLayout: dark ? "#111827" : design === "slate" ? "#eef2f6" : "#f7f8ff",
      colorBgContainer: dark ? "#182230" : "#ffffff",
      colorBorderSecondary: dark ? "#2b3a4e" : design === "lightence" ? "#e8eaff" : "#e8ecf1",
      borderRadius: design === "lightence" ? 14 : design === "slate" ? 6 : 8,
      fontSize: 13,
      fontFamily: "Inter, ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, \"Segoe UI\", sans-serif",
    },
    components: {
      Button: { borderRadius: design === "lightence" ? 10 : 6 },
      Card: { borderRadiusLG: design === "lightence" ? 16 : design === "slate" ? 7 : 8 },
      Menu: { itemBorderRadius: design === "lightence" ? 10 : 6 },
    },
  };
}

export function ThemeProvider({ children }: PropsWithChildren) {
  const [appearance, setAppearance] = useState<Appearance>(readAppearance);

  useEffect(() => {
    document.documentElement.dataset.design = appearance.design;
    document.documentElement.dataset.colorMode = appearance.mode;
    document.body.dataset.design = appearance.design;
    document.body.dataset.colorMode = appearance.mode;
    try {
      localStorage.setItem(storageKey(), JSON.stringify(appearance));
    } catch {
      // Appearance is optional enhancement; blocked storage must not break the UI.
    }
  }, [appearance]);

  const refreshProfile = () => setAppearance(readAppearance());
  const value: ThemeContextValue = {
    ...appearance,
    setDesign: (design) => setAppearance((current) => ({ ...current, design })),
    setMode: (mode) => setAppearance((current) => ({ ...current, mode })),
    refreshProfile,
  };

  return <ThemeContext.Provider value={value}><ConfigProvider theme={configFor(appearance)}>{children}</ConfigProvider></ThemeContext.Provider>;
}

export function useAppearance() {
  const value = useContext(ThemeContext);
  if (!value) throw new Error("useAppearance must be used within ThemeProvider");
  return value;
}
